import React from "react";
import { Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, CircularProgress, Button } from "@mui/material";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";

import { getAPIDataParamsReturn, postApi } from "../../utils/apiFunction";
import SelectFarmWarehouse from "../../components/SelectFarmWarehouse";
import { numberFormat } from "../../utils/numberFunctions";

const EquipmentRegistryPage = (props) => {
    const [warehouse, setWarehouse] = React.useState(null);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [editValues, setEditValues] = React.useState({}); // unitTypeId -> available
    const [hasEditAccess, setHasEditAccess] = React.useState(false);

    const loadData = React.useCallback(async (wId) => {
        if (!wId) {
            setData(null);
            return;
        }
        setLoading(true);
        try {
            const dt = await getAPIDataParamsReturn("/Farms/EquipmentRegistry", { warehouseId: wId });
            setData(dt || []);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (warehouse && warehouse.warehouseId) {
            loadData(warehouse.warehouseId);
        }
    }, [warehouse, loadData]);

    React.useEffect(() => {
        if (!hasEditAccess && editing) {
            setEditing(false);
            setEditValues({});
        }
    }, [hasEditAccess, editing]);

    const beginEditAll = () => {
        if (!data) return;
        const initial = {};
        data.forEach((row) => {
            const inUse = row.unitsInUse || 0;
            const total = row.total != null ? row.total : inUse;
            const available = Math.max(total - inUse, 0);
            initial[row.unitTypeId] = available;
        });
        setEditValues(initial);
        setEditing(true);
    };

    const saveAll = async () => {
        if (!warehouse || !warehouse.warehouseId || !data) {
            setEditing(false);
            return;
        }

        const requests = data.map((row) => {
            const inUse = row.unitsInUse || 0;
            const newAvailableRaw = editValues[row.unitTypeId];
            const newAvailable = newAvailableRaw === undefined || newAvailableRaw === null
                ? Math.max((row.total != null ? row.total : inUse) - inUse, 0)
                : Number(newAvailableRaw) || 0;
            const total = inUse + newAvailable;
            return postApi("/Farms/EquipmentRegistry", {
                warehouseId: warehouse.warehouseId,
                unitTypeId: row.unitTypeId,
                total,
            });
        });

        await Promise.all(requests);
        setEditing(false);
        setEditValues({});
        loadData(warehouse.warehouseId);
    };

    const totalInUse = data ? data.reduce((p, c) => p + (c.unitsInUse || 0), 0) : 0;
    const totalTotal = data ? data.reduce((p, c) => p + (c.total != null ? c.total : (c.unitsInUse || 0)), 0) : 0;
    const totalAvailable = data
        ? data.reduce((p, c) => {
            const inUse = c.unitsInUse || 0;
            const total = c.total != null ? c.total : inUse;
            const baseAvailable = Math.max(total - inUse, 0);
            const override = editValues[c.unitTypeId];
            const available = editing && override !== undefined && override !== null
                ? Number(override) || 0
                : baseAvailable;
            return p + available;
        }, 0)
        : 0;
    console.log(props)
    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>

                <SelectFarmWarehouse normal setState={setWarehouse} state={warehouse} access={props.access} disabled={false} title="Farm" hasEditAccess={setHasEditAccess} />

            </Grid>
            <Grid item xs={12}>
                <JumboCardQuick
                    title={
                        <span>
                            <Typography variant="h2" mb={0}>Equipment Registry</Typography>
                            <span style={{ float: "right" }}>
                                {hasEditAccess ? (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={!data || data.length === 0 || loading}
                                        onClick={editing ? saveAll : beginEditAll}
                                    >
                                        {editing ? "Save" : "Edit"}
                                    </Button>
                                ) : null}
                            </span>
                        </span>
                    }
                    wrapperSx={{
                        p: 0,
                        backgroundColor: "background.paper",
                        zIndex: 0,
                        "&:last-child": {
                            pb: 0,
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress sx={{ m: 2 }} />
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Unit Type</TableCell>
                                        <TableCell sx={{ pl: 3, textAlign: "right" }}>In Use</TableCell>
                                        <TableCell sx={{ pl: 3, textAlign: "right" }}>Available</TableCell>
                                        <TableCell sx={{ pl: 3, textAlign: "right" }}>Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data && data.map((row) => {
                                        const inUse = row.unitsInUse || 0;
                                        const total = row.total != null ? row.total : inUse;
                                        const baseAvailable = Math.max(total - inUse, 0);
                                        const currentAvailable = editing && editValues[row.unitTypeId] !== undefined && editValues[row.unitTypeId] !== null
                                            ? editValues[row.unitTypeId]
                                            : baseAvailable;
                                        return (
                                            <TableRow key={row.unitTypeId} hover>
                                                <TableCell sx={{ pl: 3 }}>{row.unitType}</TableCell>
                                                <TableCell sx={{ pl: 3, textAlign: "right" }}>{numberFormat(inUse)}</TableCell>
                                                <TableCell sx={{ pl: 3, textAlign: "right" }}>
                                                    {editing ? (
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={currentAvailable}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setEditValues((prev) => ({
                                                                    ...prev,
                                                                    [row.unitTypeId]: value,
                                                                }));
                                                            }}
                                                            inputProps={{ min: 0 }}
                                                        />
                                                    ) : (
                                                        numberFormat(baseAvailable)
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ pl: 3, textAlign: "right" }}>{numberFormat(total)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow>
                                        <TableCell>
                                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                Total
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "right" }}>
                                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                {numberFormat(totalInUse)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "right" }}>
                                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                {numberFormat(totalAvailable)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "right" }}>
                                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                {numberFormat(totalTotal)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </JumboCardQuick>
            </Grid>
        </Grid>
    );
};

export default EquipmentRegistryPage;
